// Auto-generated by @decocms/blocks-cli/scripts/generate-invoke.ts
// Do not edit manually. Re-run the generator to update.
//
// Each server function is a top-level const so TanStack Start's compiler
// can transform createServerFn().handler() into RPC stubs on the client.
//
// Site-specific extensions: import { vtexActions } from this file and merge
// with your own actions in a separate invoke.ts.
import { createServerFn } from "@tanstack/react-start";
import { getOrCreateCart, addItemsToCart, updateCartItems, addCouponToCart, getOrCreateCartV2, addItemsToCartV2, updateCartItemsV2, addCouponToCartV2, simulateCart, getSellersByRegion, setShippingPostalCode, updateOrderFormAttachment } from "@decocms/apps-vtex/actions/checkout";
import { createSession, editSession } from "@decocms/apps-vtex/actions/session";
import { subscribe } from "@decocms/apps-vtex/actions/newsletter";
import { notifyMe } from "@decocms/apps-vtex/actions/misc";
import type { OrderForm } from "@decocms/apps-vtex/types";
import type { CartProjection, CartSection } from "@decocms/apps-commerce/types";
import type { VtexCartProjectionResult } from "@decocms/apps-vtex/utils/cartProjection";
import type { SimulationItem, RegionResult } from "@decocms/apps-vtex/actions/checkout";
import type { SessionData } from "@decocms/apps-vtex/actions/session";
import type { SubscribeProps } from "@decocms/apps-vtex/actions/newsletter";
import type { NotifyMeProps } from "@decocms/apps-vtex/actions/misc";
import {
  getResponseHeaders,
  setResponseHeader,
} from "@tanstack/react-start/server";
import { RequestContext } from "@decocms/blocks/sdk/requestContext";

function unwrapResult<T>(result: unknown): T {
  if (result && typeof result === "object" && "data" in result) {
    return (result as { data: T }).data;
  }
  return result as T;
}

/**
 * Forward Set-Cookie headers captured in RequestContext.responseHeaders
 * (by vtexFetchWithCookies) into TanStack Start's HTTP response.
 *
 * Without this bridge, HttpOnly cookies like `checkout.vtex.com` and
 * `CheckoutOrderFormOwnership` that VTEX returns on cart-action responses
 * stay trapped inside the AsyncLocalStorage-backed RequestContext and
 * never reach the browser. The storefront's mini-cart drifts away from
 * VTEX's server-side orderForm, and the user lands on /checkout with an
 * empty cart.
 *
 * Cheap no-op when no Set-Cookie was captured (e.g. read-only actions),
 * so every handler can call it unconditionally without branching on
 * whether the underlying action uses vtexFetchWithCookies.
 */
function forwardResponseCookies(): void {
  const ctx = RequestContext.current;
  if (!ctx) return;
  const captured =
    typeof ctx.responseHeaders.getSetCookie === "function"
      ? ctx.responseHeaders.getSetCookie()
      : [];
  if (captured.length === 0) return;
  const existing =
    typeof getResponseHeaders().getSetCookie === "function"
      ? getResponseHeaders().getSetCookie()
      : [];
  setResponseHeader("set-cookie", [...existing, ...captured]);
}

// ---------------------------------------------------------------------------
// Top-level server function declarations
// ---------------------------------------------------------------------------

const $getOrCreateCart = createServerFn({ method: "POST" })
  .inputValidator((data: { orderFormId?: string }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await getOrCreateCart(data);
    forwardResponseCookies();
    return result;
  });

const $addItemsToCart = createServerFn({ method: "POST" })
  .inputValidator((data: {
          orderFormId: string;
          orderItems: Array<{
            id: string;
            seller: string;
            quantity: number;
          }>;
        }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await addItemsToCart(data);
    forwardResponseCookies();
    return result;
  });

const $updateCartItems = createServerFn({ method: "POST" })
  .inputValidator((data: { orderFormId: string; orderItems: Array<{ index: number; quantity: number }> }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await updateCartItems(data);
    forwardResponseCookies();
    return result;
  });

const $addCouponToCart = createServerFn({ method: "POST" })
  .inputValidator((data: { orderFormId: string; text: string }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await addCouponToCart(data);
    forwardResponseCookies();
    return result;
  });

const $getOrCreateCartV2 = createServerFn({ method: "POST" })
  .inputValidator((data: { orderFormId?: string; projection?: CartProjection; sections?: CartSection[] }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await getOrCreateCartV2(data);
    forwardResponseCookies();
    return result;
  });

const $addItemsToCartV2 = createServerFn({ method: "POST" })
  .inputValidator((data: {
          orderFormId: string;
          orderItems: Array<{ id: string; seller: string; quantity: number }>;
          projection?: CartProjection;
          sections?: CartSection[];
        }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await addItemsToCartV2(data);
    forwardResponseCookies();
    return result;
  });

const $updateCartItemsV2 = createServerFn({ method: "POST" })
  .inputValidator((data: {
          orderFormId: string;
          orderItems: Array<{ index: number; quantity: number }>;
          projection?: CartProjection;
          sections?: CartSection[];
        }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await updateCartItemsV2(data);
    forwardResponseCookies();
    return result;
  });

const $addCouponToCartV2 = createServerFn({ method: "POST" })
  .inputValidator((data: {
          orderFormId: string;
          text: string;
          projection?: CartProjection;
          sections?: CartSection[];
        }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await addCouponToCartV2(data);
    forwardResponseCookies();
    return result;
  });

const $simulateCart = createServerFn({ method: "POST" })
  .inputValidator((data: { items: SimulationItem[]; postalCode: string; country?: string }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await simulateCart(data);
    forwardResponseCookies();
    return result;
  });

const $getSellersByRegion = createServerFn({ method: "POST" })
  .inputValidator((data: { postalCode: string; salesChannel?: string }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await getSellersByRegion(data);
    forwardResponseCookies();
    return result;
  });

const $setShippingPostalCode = createServerFn({ method: "POST" })
  .inputValidator((data: { orderFormId: string; postalCode: string; country?: string }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await setShippingPostalCode(data);
    forwardResponseCookies();
    return result;
  });

const $updateOrderFormAttachment = createServerFn({ method: "POST" })
  .inputValidator((data: { orderFormId: string; attachment: string; body: Record<string, unknown> }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await updateOrderFormAttachment(data);
    forwardResponseCookies();
    return result;
  });

const $createSession = createServerFn({ method: "POST" })
  .inputValidator((data: Record<string, any>) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await createSession({ data });
    forwardResponseCookies();
    return result;
  });

const $editSession = createServerFn({ method: "POST" })
  .inputValidator((data: { public: Record<string, { value: string }> }) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await editSession(data);
    forwardResponseCookies();
    return result;
  });

const $subscribe = createServerFn({ method: "POST" })
  .inputValidator((data: SubscribeProps) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await subscribe(data);
    forwardResponseCookies();
    return result;
  });

const $notifyMe = createServerFn({ method: "POST" })
  .inputValidator((data: NotifyMeProps) => data)
  .handler(async ({ data }): Promise<any> => {
    const result = await notifyMe(data);
    forwardResponseCookies();
    return result;
  });

// ---------------------------------------------------------------------------
// Typed VTEX actions map — merge with site-specific actions in your invoke.ts
// ---------------------------------------------------------------------------

export const vtexActions = {
  getOrCreateCart: $getOrCreateCart as unknown as (ctx: { data: { orderFormId?: string } }) => Promise<OrderForm>,
  addItemsToCart: $addItemsToCart as unknown as (ctx: { data: {
          orderFormId: string;
          orderItems: Array<{
            id: string;
            seller: string;
            quantity: number;
          }>;
        } }) => Promise<OrderForm>,
  updateCartItems: $updateCartItems as unknown as (ctx: { data: { orderFormId: string; orderItems: Array<{ index: number; quantity: number }> } }) => Promise<OrderForm>,
  addCouponToCart: $addCouponToCart as unknown as (ctx: { data: { orderFormId: string; text: string } }) => Promise<OrderForm>,
  getOrCreateCartV2: $getOrCreateCartV2 as unknown as (ctx: { data: { orderFormId?: string; projection?: CartProjection; sections?: CartSection[] } }) => Promise<VtexCartProjectionResult>,
  addItemsToCartV2: $addItemsToCartV2 as unknown as (ctx: { data: {
          orderFormId: string;
          orderItems: Array<{ id: string; seller: string; quantity: number }>;
          projection?: CartProjection;
          sections?: CartSection[];
        } }) => Promise<VtexCartProjectionResult>,
  updateCartItemsV2: $updateCartItemsV2 as unknown as (ctx: { data: {
          orderFormId: string;
          orderItems: Array<{ index: number; quantity: number }>;
          projection?: CartProjection;
          sections?: CartSection[];
        } }) => Promise<VtexCartProjectionResult>,
  addCouponToCartV2: $addCouponToCartV2 as unknown as (ctx: { data: {
          orderFormId: string;
          text: string;
          projection?: CartProjection;
          sections?: CartSection[];
        } }) => Promise<VtexCartProjectionResult>,
  simulateCart: $simulateCart,
  getSellersByRegion: $getSellersByRegion as unknown as (ctx: { data: { postalCode: string; salesChannel?: string } }) => Promise<RegionResult | null>,
  setShippingPostalCode: $setShippingPostalCode as unknown as (ctx: { data: { orderFormId: string; postalCode: string; country?: string } }) => Promise<boolean>,
  updateOrderFormAttachment: $updateOrderFormAttachment as unknown as (ctx: { data: { orderFormId: string; attachment: string; body: Record<string, unknown> } }) => Promise<OrderForm>,
  createSession: $createSession,
  editSession: $editSession as unknown as (ctx: { data: { public: Record<string, { value: string }> } }) => Promise<SessionData>,
  subscribe: $subscribe as unknown as (ctx: { data: SubscribeProps }) => Promise<void>,
  notifyMe: $notifyMe as unknown as (ctx: { data: NotifyMeProps }) => Promise<void>,
} as const;

// Re-export OrderForm type (commonly imported from invoke by site components)
export type { OrderForm } from "@decocms/apps-vtex/types";

// ---------------------------------------------------------------------------
// Default invoke object — import this if you don't need site extensions
// ---------------------------------------------------------------------------

export const invoke = {
  vtex: {
    actions: vtexActions,
  },
} as const;
